import { useState, useEffect } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { hrAPI } from "../../services/api";
import EmployeeList from "./EmployeeList";
import EmployeeProfile from "./EmployeeProfile";
import AddEmployee from "./AddEmployee";
import { Users, UserPlus, LayoutDashboard, TrendingUp, AlertCircle, DollarSign, UserCheck, Search } from "lucide-react";

type Tab = "dashboard" | "employees" | "add";

export default function HR() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [tab, setTab] = useState<Tab>("dashboard");
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [filterDept, setFilterDept] = useState("");

  useEffect(() => { if (tab === "dashboard") loadDashboard(); }, [tab]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const r = await hrAPI.dashboard();
      if (r.success) setStats(r);
    } catch {}
    setLoading(false);
  };

  const tabs = [
    { id: "dashboard", label: "داشبورد",    icon: LayoutDashboard },
    { id: "employees", label: "کارمندان",   icon: Users },
    { id: "add",       label: "کارمند جدید", icon: UserPlus },
  ];

  const fmt = (n: number) => new Intl.NumberFormat("en-US").format(n || 0);

  if (selectedEmployee) {
    return <EmployeeProfile employeeId={selectedEmployee} onBack={() => setSelectedEmployee(null)} />;
  }

  return (
    <div style={{ padding: "28px 28px 40px", fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }} dir="rtl">

      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 11, color: "#8b5cf6", fontWeight: 700, letterSpacing: "1.5px", marginBottom: 6 }}>👥 منابع انسانی</div>
        <h1 style={{ fontSize: 26, fontWeight: 900, margin: 0, color: T.text }}>مدیریت منابع انسانی</h1>
        <p style={{ fontSize: 13, color: T.subtext, margin: "5px 0 0" }}>مدیریت کارمندان، قراردادها، معاشات و قرضه‌ها</p>
      </div>

      {/* تب‌ها */}
      <div style={{ display: "flex", gap: 8, marginBottom: 28, background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)", border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 6 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id as Tab); setFilterDept(""); }} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "10px 16px", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit", background: tab === t.id ? "linear-gradient(135deg,#8b5cf6,#6d28d9)" : "transparent", color: tab === t.id ? "white" : T.subtext, transition: "all 0.2s" }}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* ── داشبورد ── */}
      {tab === "dashboard" && (
        loading ? (
          <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>
            <div style={{ width: 40, height: 40, border: "3px solid rgba(139,92,246,0.2)", borderTop: "3px solid #8b5cf6", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
            در حال بارگذاری...
          </div>
        ) : (
          <div>
            {/* جستجو */}
            <div style={{ position: "relative", marginBottom: 20 }}>
              <Search size={14} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: T.subtext }} />
              <input
                value={search}
                onChange={e => { setSearch(e.target.value); if (e.target.value) { setTab("employees"); } }}
                placeholder="جستجوی کارمند به نام، کد، شماره تماس..."
                style={{ width: "100%", padding: "12px 42px 12px 14px", background: dark ? "rgba(255,255,255,0.05)" : "#fff", border: `1.5px solid ${T.cardBorder}`, borderRadius: 12, color: T.text, fontSize: 14, fontFamily: "inherit", direction: "rtl", outline: "none", boxSizing: "border-box" as const }}
              />
            </div>

            {/* کارت‌های آمار */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 28 }}>
              {[
                { label: "کارمندان فعال",       value: fmt(stats?.stats?.total_employees),  color: "#8b5cf6", icon: UserCheck,   suffix: "نفر" },
                { label: "کارمند جدید این ماه", value: fmt(stats?.stats?.new_this_month),   color: "#10b981", icon: TrendingUp,  suffix: "نفر" },
                { label: "معاش در انتظار",       value: fmt(stats?.stats?.pending_salaries), color: "#f59e0b", icon: AlertCircle, suffix: "پرداخت" },
                { label: "مجموع قرضه فعال",      value: fmt(stats?.stats?.total_loans),      color: "#ef4444", icon: DollarSign,  suffix: "" },
              ].map((card, i) => (
                <div key={i} style={{ borderRadius: 16, padding: 20, background: dark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${T.cardBorder}`, borderTop: `3px solid ${card.color}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: `${card.color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <card.icon size={18} color={card.color} />
                    </div>
                    <div style={{ fontSize: 12, color: T.subtext, fontWeight: 600 }}>{card.label}</div>
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: card.color }}>{card.value}</div>
                  {card.suffix && <div style={{ fontSize: 11, color: T.subtext, marginTop: 2 }}>{card.suffix}</div>}
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {/* بخش‌ها - کلیک‌پذیر */}
              <div style={{ borderRadius: 16, padding: 20, background: dark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${T.cardBorder}` }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 16 }}>👔 کارمندان بر اساس بخش</div>
                {(stats?.departments || []).length === 0
                  ? <div style={{ color: T.subtext, fontSize: 13, textAlign: "center", padding: 20 }}>هیچ بخشی ثبت نشده</div>
                  : (stats?.departments || []).map((d: any, i: number) => (
                    <div key={i} onClick={() => { setFilterDept(d.department); setTab("employees"); }}
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderBottom: `1px solid ${T.cardBorder}`, cursor: "pointer", borderRadius: 8, transition: "background 0.15s" }}
                      onMouseEnter={e => (e.currentTarget.style.background = dark ? "rgba(139,92,246,0.08)" : "rgba(139,92,246,0.05)")}
                      onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                    >
                      <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{d.department}</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#8b5cf6" }}>{d.cnt} نفر</span>
                        <span style={{ fontSize: 11, color: T.subtext }}>← مشاهده</span>
                      </div>
                    </div>
                  ))
                }
              </div>

              {/* کارمندان اخیر */}
              <div style={{ borderRadius: 16, padding: 20, background: dark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${T.cardBorder}` }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 16 }}>🆕 آخرین کارمندان</div>
                {(stats?.recent_employees || []).map((e: any, i: number) => (
                  <div key={i} onClick={() => setSelectedEmployee(e.id)}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 8px", borderBottom: `1px solid ${T.cardBorder}`, cursor: "pointer", borderRadius: 8, transition: "background 0.15s" }}
                    onMouseEnter={el => (el.currentTarget.style.background = dark ? "rgba(139,92,246,0.08)" : "rgba(139,92,246,0.05)")}
                    onMouseLeave={el => (el.currentTarget.style.background = "transparent")}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "white", fontWeight: 700, overflow: "hidden", flexShrink: 0 }}>
                        {e.photo_url
                          ? <img src={e.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : `${e.first_name?.[0]}${e.last_name?.[0]}`
                        }
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{e.first_name} {e.last_name}</div>
                        <div style={{ fontSize: 11, color: T.subtext }}>{e.position || "—"} • {e.department || "—"}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: "left" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#8b5cf6" }}>{fmt(e.base_salary)} {e.currency || ""}</div>
                      <div style={{ fontSize: 10, color: e.status === "active" ? "#10b981" : "#ef4444" }}>{e.status === "active" ? "فعال" : "غیرفعال"}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      )}

      {tab === "employees" && (
        <EmployeeList
          initialSearch={search}
          initialDept={filterDept}
          onSelect={(id) => setSelectedEmployee(id)}
        />
      )}

      {tab === "add" && (
        <AddEmployee onSuccess={() => { setTab("employees"); loadDashboard(); }} />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}